import React, { useMemo, useState } from "react";
import { CheckCircle, Clock, ChevronDown, ChevronUp, Package } from "lucide-react";

function formatShortDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

/**
 * @param {{ order?: object }} props
 */
const TrackOrderTimeline = ({ order }) => {
  const [viewMore, setViewMore] = useState(false);

  const steps = useMemo(() => {
    if (!order) {
      return [
        {
          key: "placeholder",
          title: "Tracking",
          subtitle: "Open this order again after it loads.",
          state: "upcoming",
        },
      ];
    }

    const rejected = order.sellerDecision === "rejected";
    const approved = order.sellerDecision === "approved";
    const pendingSeller = order.sellerDecision === "pending";
    const ready = Boolean(order.itemReadyAt);
    const shipped = order.status === "shipped" || order.status === "delivered";
    const delivered = order.status === "delivered";

    const receiving = String(
      order.shippingInfo?.receivingMethod || "delivery",
    ).toLowerCase();
    const isPickup = receiving === "pickup";
    const readyLabel = isPickup ? "Ready for pickup" : "Ready for delivery";

    /** state: done | current | upcoming | error */
    const row = (key, title, subtitle, done, current, err = false) => ({
      key,
      title,
      subtitle,
      state: err ? "error" : done ? "done" : current ? "current" : "upcoming",
    });

    let sellerSubtitle = "";
    if (rejected) sellerSubtitle = order.rejectionReason || "The seller could not accept this order.";
    else if (pendingSeller) sellerSubtitle = "Waiting for the seller to accept or decline.";
    else if (approved) sellerSubtitle = formatShortDate(order.sellerDecisionAt || order.createdAt);

    return [
      row("placed", "Order placed", formatShortDate(order.createdAt), true, false),
      row(
        "seller",
        rejected ? "Seller declined" : "Seller accepted",
        sellerSubtitle,
        approved || rejected,
        pendingSeller,
        rejected,
      ),
      row(
        "ready",
        readyLabel,
        ready
          ? formatShortDate(order.itemReadyAt)
          : approved
            ? "The seller will mark this when your items are prepared."
            : "—",
        ready,
        approved && !rejected && !ready && !pendingSeller,
      ),
      row(
        "shipped",
        "Shipped",
        shipped ? "In transit to you" : "Not yet shipped",
        shipped,
        ready && !shipped,
      ),
      row(
        "delivered",
        "Delivered",
        delivered ? "Completed" : "Not yet delivered",
        delivered,
        shipped && !delivered,
      ),
    ];
  }, [order]);

  const currentIndex = useMemo(() => {
    const i = steps.findIndex((s) => s.state === "current");
    if (i >= 0) return i;
    const d = steps.findIndex((s) => s.state === "error");
    if (d >= 0) return d;
    return steps.length - 1;
  }, [steps]);

  const visibleSteps = viewMore ? steps : [steps[currentIndex] || steps[0]].filter(Boolean);

  const iconWrap = (state) => {
    if (state === "error") return "bg-red-100 text-red-700 ring-2 ring-red-200";
    if (state === "done") return "bg-emerald-600 text-white shadow-md shadow-emerald-200/50";
    if (state === "current") return "bg-amber-100 text-amber-900 ring-2 ring-amber-300";
    return "bg-stone-200 text-stone-500";
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-5">
      <div className="mb-3 flex items-center gap-2">
        <Package className="h-4 w-4 text-orange-600" strokeWidth={2.25} aria-hidden />
        <h2 className="text-sm font-bold uppercase tracking-wide text-stone-600">
          Order progress
        </h2>
      </div>

      <div className="flex flex-col gap-4">
        {visibleSteps.map((step) => (
          <div key={step.key} className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${iconWrap(
                step.state,
              )}`}
            >
              {step.state === "done" ? (
                <CheckCircle className="h-4 w-4" strokeWidth={2.5} aria-hidden />
              ) : (
                <Clock className="h-4 w-4" strokeWidth={2.25} aria-hidden />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p
                className={`text-sm font-semibold ${
                  step.state === "error"
                    ? "text-red-800"
                    : step.state === "done"
                      ? "text-emerald-800"
                      : step.state === "current"
                        ? "text-amber-900"
                        : "text-stone-600"
                }`}
              >
                {step.title}
              </p>
              {step.subtitle && step.subtitle !== "—" ? (
                <p className="mt-0.5 text-xs leading-relaxed text-stone-500">{step.subtitle}</p>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {steps.length > 1 ? (
        !viewMore ? (
          <button
            type="button"
            className="mt-4 flex items-center text-sm font-semibold text-orange-700 hover:text-orange-900"
            onClick={() => setViewMore(true)}
          >
            View full timeline <ChevronDown className="ml-1 h-4 w-4" aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            className="mt-4 flex items-center text-sm font-semibold text-orange-700 hover:text-orange-900"
            onClick={() => setViewMore(false)}
          >
            Show less <ChevronUp className="ml-1 h-4 w-4" aria-hidden />
          </button>
        )
      ) : null}
    </div>
  );
};

export default TrackOrderTimeline;
